/**
 * Consolidated dye configuration for Patch 7.5 (TRAIL TO THE HEAVENS, 2026-04-28).
 *
 * itemIDs (52254 / 52255 / 52256) and all six localized names are populated.
 * The `ko: string | null` / `zh: string | null` types are kept as a safety hatch
 * for future consolidation patches that may ship before localized names are sourced.
 */
export const CONSOLIDATED_IDS = {
    A: 52254, // Standard Spectrum Dye — covers the 85 ARR dyes (itemIDs 5729-5813)
    B: 52255, // Wide Spectrum #1 Dye — covers the 9 Ishgardian Restoration dyes (itemIDs 30116-30124)
    C: 52256, // Wide Spectrum #2 Dye — covers the 11 Cosmic Exploration dyes (itemIDs 48163-48172, 48227)
};
/**
 * Full metadata for the three consolidated dye items shipped in Patch 7.5.
 * `itemID` mirrors `CONSOLIDATED_IDS` and is filled on patch day.
 */
export const CONSOLIDATED_DYES = {
    A: {
        itemID: CONSOLIDATED_IDS.A,
        names: {
            en: 'Standard Spectrum Dye',
            ja: 'カララント:ノーマルカラー',
            de: 'Einfacher Farbstoff',
            fr: 'Teinture standard',
            ko: '염료: 기본 색상',
            zh: '通用染剂',
        },
        acquisition: 'Dye Vendor',
        price: 216,
        currency: 'Gil',
    },
    B: {
        itemID: CONSOLIDATED_IDS.B,
        names: {
            en: 'Wide Spectrum #1 Dye',
            ja: 'カララント:アディショナルカラー1',
            de: 'Zusatzfarbstoff 1',
            fr: 'Teinture additionnelle n°1',
            ko: '염료: 추가 색상 1',
            zh: '追加染剂1',
        },
        acquisition: 'The Firmament',
        price: 100,
        currency: 'Skybuilders Scrips',
    },
    C: {
        itemID: CONSOLIDATED_IDS.C,
        names: {
            en: 'Wide Spectrum #2 Dye',
            ja: 'カララント:アディショナルカラー2',
            de: 'Zusatzfarbstoff 2',
            fr: 'Teinture additionnelle n°2',
            ko: '염료: 추가 색상 2',
            zh: '追加染剂2',
        },
        acquisition: 'Cosmic Exploration',
        price: 600,
        currency: 'Cosmocredits',
    },
};
/**
 * Whether dye consolidation is active (all IDs are known).
 * Returns true only when all three consolidated IDs have been set.
 */
export function isConsolidationActive() {
    return (CONSOLIDATED_IDS.A !== null &&
        CONSOLIDATED_IDS.B !== null &&
        CONSOLIDATED_IDS.C !== null);
}
/**
 * Get the market board item ID for a dye.
 *
 * - If consolidation is active and the dye has a consolidationType, returns the consolidated ID.
 * - Otherwise returns the dye's original itemID.
 * - Facewear dyes (negative IDs) always return the original ID.
 */
export function getMarketItemID(dye) {
    if (dye.itemID < 0)
        return dye.itemID; // Facewear
    if (!dye.consolidationType)
        return dye.itemID; // Special/uncategorized
    if (!isConsolidationActive())
        return dye.itemID; // Pre-patch fallback
    return CONSOLIDATED_IDS[dye.consolidationType];
}
/**
 * Get the localized display name for a consolidated dye.
 * Falls back to English when the requested locale's translation is `null`
 * (a safety hatch for any future unsourced ko/zh strings; not exercised today).
 */
export function getConsolidatedDyeName(type, locale) {
    const names = CONSOLIDATED_DYES[type].names;
    return names[locale] ?? names.en;
}
//# sourceMappingURL=consolidated-ids.js.map